import {
  Alert,
  Button,
  LinearProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { withTheme } from "@mui/styles";
import _ from "lodash";
import React, { Component } from "react";
import Geocode from "react-geocode";
import TagManager from "react-gtm-module";
import UserContext from "../../Contexts/UserContext";
import {
  doSearch,
  doSearchStores,
  doSubmitSuggestion,
} from "../../Services/Data.service";
import { ShowMessage } from "../../Utils/Utils";
import PlacesAutocomplete, {
  geocodeBySuggestion,
} from "../Components/PlacesAutocomplete";

Geocode.setApiKey(process.env.REACT_APP_GOOGLE_API_KEY);

const compactInputSx = {
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiFilledInput-input": {
    fontSize: 13,
    paddingTop: "17px",
    paddingBottom: "3px",
  },
};

const compactAutocompleteSx = {
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiFilledInput-root": { paddingTop: "15px" },
  "& .MuiFilledInput-input": { fontSize: 13 },
};

class SuggestStoreForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      coordinates: null,
      loading: false,
      term: "",
      zipcode: null,
      geopoliticalRegion: null,
      name: "",
      email: "",
      description: "",
      chain: "",
      searchOpen: false,
      suggestSuccess: false,
      foundStore: null,
      foundStoreOutside: null,
    };
  }

  onSuggestionSelected = (suggestion) => {
    this.setState({
      term: suggestion.description,
      geopoliticalRegion: suggestion.types[0],
    });
    geocodeBySuggestion(suggestion)
      .then((results) => {
        if (results.length < 1) {
          ShowMessage(
            "Couldn't find the location you entered, please try being more specific",
          );
        }

        const zipcodeIndex = _.findKey(results[0].address_components, {
          types: ["postal_code"],
        });
        const zipcode = zipcodeIndex
          ? results[0].address_components[zipcodeIndex].long_name
          : "";

        const { geometry } = results[0];

        const coordinates = {
          lat: geometry.location.lat(),
          lng: geometry.location.lng(),
        };

        this.setState({
          coordinates: [coordinates.lat, coordinates.lng],
          zipcode: zipcode,
        });

        TagManager.dataLayer({
          dataLayer: {
            event: "searchChange",
            category: "Search",
            action: "Zipcode (from google places): " + zipcode,
          },
        });
        return doSearchStores(
          [
            {
              address: results?.[0]?.formatted_address,
              zipcode,
              latitude: coordinates.lat,
              longitude: coordinates.lng,
              chain: this.state.chain,
              secondary_images: [],
            },
          ],
          true,
          undefined,
          window.DATHIC_ORG_ID,
        );
      })
      .then((searchResult) => {
        if (
          searchResult?.[0]?.store_validation === "found" &&
          searchResult?.[0]?.store_id
        ) {
          this.setState({ foundStore: searchResult?.[0] });
        } else {
          return doSearchStores(
            [
              {
                address: searchResult?.[0]?.address,
                zipcode: searchResult?.[0]?.zipcode,
                latitude: searchResult?.[0]?.latitude,
                longitude: searchResult?.[0]?.longitude,
                chain: searchResult?.[0]?.chain,
                secondary_images: [],
              },
            ],
            false,
          );
        }
        return undefined;
      })
      .then((searchResult) => {
        if (
          searchResult?.[0]?.store_validation === "found" &&
          searchResult?.[0]?.store_id
        ) {
          this.setState({ foundStoreOutside: searchResult?.[0] });
        }
      })
      .catch((err) => {
        ShowMessage(undefined, {
          message: err.message,
          filename: "SuggestStoreForm.js:geocodeBySuggestion",
          error: err,
        });
      });
  };

  handleKeyDown = (event) => {
    this.setState({ term: event.target.value });

    if (event.key === "Enter") {
      this.searchWithTerm();
    }
  };

  searchWithTerm = async () => {
    const { term } = this.state;
    if (term === "") {
      ShowMessage("Please enter a valid address or zipcode");
      return;
    }
    TagManager.dataLayer({
      dataLayer: {
        event: "searchChange",
        category: "Search",
        action: "Zipcode or Address:" + term,
      },
    });
    this.setState({ loading: true });

    const data = await Geocode.fromAddress(term);

    this.setState({ loading: false });
    if (data.results[0]) {
      const { geometry } = data.results[0];

      const coordinates = {
        lat: geometry.location.lat,
        lng: geometry.location.lng,
      };

      const zipcodeIndex = _.findKey(data.results[0].address_components, {
        types: ["postal_code"],
      });
      const zipcode = zipcodeIndex
        ? data.results[0].address_components[zipcodeIndex].long_name
        : "";

      this.setState({
        coordinates: [coordinates.lat, coordinates.lng],
        zipcode: zipcode,
      });
    }
  };

  onSubmit = async (e) => {
    const {
      coordinates,
      name,
      email,
      description,
      term,
      chain,
      foundStoreOutside,
    } = this.state;
    if (!chain) {
      ShowMessage("Please enter the retail / store name");
      return false;
    }
    if (!coordinates || coordinates.length === 0) {
      ShowMessage("Please be more specific with your address");
      return false;
    }

    const suggestion = {
      customer: {
        name: name || undefined,
        email: email || undefined,
      },
      latitude: coordinates?.[0],
      longitude: coordinates?.[1],
      address: term,
      chain,
      description,
      secondary_images: [],
      parent_id: foundStoreOutside?.store_id,
    };

    doSubmitSuggestion(suggestion).then((response) => {
      if (response?.id) {
        const { onSuggest } = this.props;
        this.setState({
          name: "",
          email: "",
          description: "",
          chain: "",
          coordinates: [],
          term: "",
          suggestSuccess: true,
        });
        ShowMessage("Thank you for your store suggestion!");
        if (onSuggest) onSuggest();
      }
    });
    return false;
  };

  goToStore(e) {
    const { foundStore } = this.state;
    this.setState({ goingToStore: true });
    doSearch(
      [foundStore.latitude, foundStore.longitude],
      foundStore.zipcode,
      11,
      undefined,
      undefined,
      undefined,
    )
      .then((stores) => {
        const store = stores?.find((s) => s.id === foundStore.store_id);
        this.props.showResultDetailsPage(store, e);
      })
      .finally(() => {
        this.setState({ goingToStore: false });
      });
  }

  render() {
    const { storeNameReplacement = "Store" } =
      this.context?.state?.orgProperties || {};
    const config = this.context?.state?.config || {};
    const {
      foundStore,
      name,
      email,
      searchOpen,
      term,
      suggestSuccess,
      chain,
      description,
      goingToStore,
    } = this.state;
    return (
      <form id="suggest_store">
        <Stack direction="column" spacing={1}>
          <TextField
            name="chain"
            label="Retail / store name"
            variant="filled"
            size="small"
            required
            fullWidth
            value={chain}
            onChange={(e) =>
              this.setState({ chain: e.target.value, foundStore: null })
            }
            sx={compactInputSx}
          />
          <PlacesAutocomplete
            open={searchOpen}
            onOpen={() => this.setState({ searchOpen: true })}
            onClose={() => this.setState({ searchOpen: false })}
            textFieldProps={{
              onKeyDown: (event) => this.handleKeyDown(event),
              onChange: (e) =>
                this.setState({
                  term: (chain || "") + e.target.value,
                  foundStore: null,
                }),
              fullWidth: true,
              margin: "dense",
              required: true,
              value: term,
              placeholder: "Zipcode / store address",
              name: "address",
              label: "Zipcode / store address",
              sx: compactAutocompleteSx,
            }}
            onSuggestionSelected={(e) => this.onSuggestionSelected(e)}
          />
          <TextField
            name="email"
            label="Your email"
            variant="filled"
            size="small"
            fullWidth
            value={email}
            onChange={(e) => this.setState({ email: e.target.value })}
            sx={compactInputSx}
          />
          <TextField
            name="name"
            label="Your name"
            variant="filled"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => this.setState({ name: e.target.value })}
            sx={compactInputSx}
          />
          <Typography variant="body2" gutterBottom>
            Help us get on their shelves!
          </Typography>
          <TextField
            name="description"
            hiddenLabel
            placeholder="Share why you’d love our products there and help us convince the store manager!"
            variant="filled"
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
            value={description}
            onChange={(e) => this.setState({ description: e.target.value })}
            multiline
            maxRows={5}
            inputProps={{
              style: {
                fontSize: "12px",
                lineHeight: "19px",
              },
            }}
          />
        </Stack>
        {foundStore && (
          <Alert severity="success" style={{ marginTop: 20 }}>
            This store was found!{" "}
            <Button
              variant="contained"
              fullWidth
              onClick={(e) => this.goToStore(e)}
              disabled={!!goingToStore}
            >
              Go to store
            </Button>
            {goingToStore && <LinearProgress />}
          </Alert>
        )}

        <Stack direction="row" alignItems={"center"} spacing={2} mt={2}>
          {this.props.onCancel && (
            <Button
              variant="text"
              color="inherit"
              onClick={() => this.props.onCancel()}
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={this.onSubmit}
            fullWidth
            variant="contained"
            className={this.props.theme.submit}
            sx={{
              color: this.props.theme.palette.primary.main,
              bgcolor: this.props.theme.palette.secondary.main,
              "&:hover": {
                color: this.props.theme.palette.primary.main,
                bgcolor: this.props.theme.palette.secondary.main,
              },
            }}
            {...config?.suggestBtnProps}
          >
            Suggest new {storeNameReplacement}
          </Button>
        </Stack>
        <Typography
          variant="caption"
          display="block"
          mt={1.5}
          sx={{
            color: this.props.theme.palette.getContrastText(
              this.props.theme.palette.primary.main,
            ),
          }}
        >
          By submitting this form, you agree to our use of your personal
          information to contact you about our products, relevant updates or
          offers.
        </Typography>
        <Snackbar
          open={suggestSuccess}
          autoHideDuration={6000}
          onClose={() => this.setState({ suggestSuccess: false })}
        >
          <Alert
            onClose={() => this.setState({ suggestSuccess: false })}
            severity="success"
            sx={{ width: "100%" }}
          >
            Store Suggested
          </Alert>
        </Snackbar>
      </form>
    );
  }
}
SuggestStoreForm.contextType = UserContext;
export default withTheme(SuggestStoreForm);
